import React, { Fragment, useEffect, useRef, useState } from "react"
import isEmpty from "validator/lib/isEmpty"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { Button, CdsamCityAndStateSelect, TextField } from "components"
import { editOrderAddress } from "services"
import classes from "./OdhAddressForm.module.scss"
import clsx from "clsx"

const initialFormState = {
  address: "",
  state: "",
  city: "",
  note: "",
  zipCode: ""
}

const initialFormErrorState = {
  address: "",
  state: "",
  city: "",
  note: ""
}

const OdhAddressForm = props => {
  const { order, setOrders } = props

  const [t, i18n] = useTranslation()

  const systemIsActivated =
    useSelector(state => state.systemSettings.systemIsActivated)

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(initialFormState)
  const [formErrors, setFormErrors] = useState(initialFormErrorState)
  const [loading, setLoading] = useState(false)

  const unmount = useRef(false)

  const handleShowForm = () => {
    setShowForm(true)

    setForm({
      zipCode: order.address.zipCode,
      address: order.address.address,
      city: order.address.city,
      state: order.address.state,
      note: order.address.note
    })
  }

  const handleChange = (value, name) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validate = () => {
    const errors = { ...initialFormErrorState }
    let result = true

    if (isEmpty(form.address)) {
      errors.address = t("آدرس الزامی است")
      result = false
    } else if (form.address.length < 10) {
      errors.address = t("آدرس کوتاه است")
      result = false
    } else if (form.address.length > 250) {
      errors.address = t("آدرس طولانی است")
      result = false
    }

    setFormErrors(errors)

    return result
  }

  const handleSave = async () => {
    if (!validate()) return

    setLoading(true)

    try {
      const response =
        await editOrderAddress(order._id, form)

      if (unmount.current) return

      setOrders(prev =>
        prev.map(o =>
          o._id === order._id ? response : o
        )
      )

      setShowForm(false)
    } catch (err) {
      console.log(err)

      toast.error(t("ویرایش انجام نشد"))
    }

    if (unmount.current) return

    setLoading(false)
  }

  useEffect(() => {
    unmount.current = false

    return () => { unmount.current = true }
  }, [])

  const showEditAddressButton =
    order.orderStatus === "pending" ||
    order.orderStatus === "paid"

  const isLtr = i18n.dir(i18n.language) === "ltr"

  return (
    <Fragment>
      <div className={classes["row"]}>
        <span className={classes["title"]}>
          {t("تحویل به")}
        </span>

        {showForm
          ?
          <div className={classes["buttons"]}>
            <Button
              loading={loading}
              className={clsx(classes["cancel"], isLtr && classes["cancel-ltr"])}
              onClick={handleSave}
              color="success"
            >
              {t("ذخیره")}
            </Button>

            <Button
              disabled={loading}
              className={classes["button"]}
              onClick={() => setShowForm(false)}
              color="danger"
            >
              {t("لغو ویرایش")}
            </Button>

          </div>
          :
          showEditAddressButton &&
          < Button
            className={classes["button"]}
            onClick={handleShowForm}
            color="warning"
          >
            {t("ویرایش")}
          </Button>
        }
      </div>
      {
        showForm
          ?
          <div className={classes["form"]}>
            <CdsamCityAndStateSelect
              form={form}
              setForm={setForm}
              formErrors={formErrors}
            />
            <TextField
              label={t("کدپستی")}
              value={form.zipCode}
              error={formErrors.zipCode}
              className={classes["input"]}
              onChange={(e) => handleChange(e.target.value, "zipCode")}
            />
            <TextField
              label={t("آدرس")}
              value={form.address}
              error={formErrors.address}
              className={classes["input"]}
              maxLength={250}
              onChange={(e) => handleChange(e.target.value, "address")}
            />
            <TextField
              label={t("یادداشت")}
              maxLength={200}
              value={form.note}
              error={formErrors.note}
              className={classes["input"]}
              onChange={(e) => handleChange(e.target.value, "note")}
            />
          </div>
          :
          <div className={classes["title-container"]}>
            <div className={classes["value"]}>
              <span>{t("استان")}:{" "}</span>
              <b>
                {order.address.state}
              </b>
            </div>

            <div className={classes["value"]}>
              <span>{t("شهر")}:{" "}</span>
              <b>
                {systemIsActivated ? order.address.city : "**********"}
              </b>
            </div>

            {
              !!order.address.zipCode.trim() &&
              <div className={classes["value"]}>
                <span>{t("کدپستی")}:{" "}</span>
                <b>
                  {order.address.zipCode}
                </b>
              </div>
            }

            <div className={classes["value"]}>
              <span>{t("آدرس")}:{" "}</span>
              <b>
                {systemIsActivated ? order.address.address : "**********"}
              </b>
            </div>

            <div className={classes["value"]}>
              <span>{t("روش_ارسال")}:{" "}</span>

              {order.deliveryService === "snapbox"
                ?
                <img
                  src="/assets/images/snappbox-logo.png"
                  className={classes["snapbox"]}
                  alt="snapbox"
                />
                :
                <b>
                  {order.areaName || order.address.areaName}
                </b>
              }
            </div>

            {
              !!order.address.note &&
              <div className={classes["address-note"]}>
                <span>{t("یادداشت")}:{" "}</span>
                <b>
                  {order.address.note}
                </b>
              </div>
            }
          </div>
      }
    </Fragment >
  )
}

export default OdhAddressForm